/*
 * Every colour, space, radius, font and shadow here is a design token.
 * The only literals are structural hairlines, the 2px sort bar and stripe, and the
 * mix percentages that derive every gold from --active.
 */

.scroller,
.scroller * {
  box-sizing: border-box;
}

/* ScrollShadow owns the overflow and the edge shadows; this only adds the corner. */
.scroller {
  width: 100%;
  border-radius: var(--radius-lg);
}

.table {
  width: 100%;
  /* separate, not collapse: collapsed borders don't stay put under a sticky header. */
  border-collapse: separate;
  border-spacing: 0;

  font-family: var(--font-sans);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-primary);
}

/* ── Header ── */

.th {
  position: sticky;
  top: 0;
  z-index: var(--z-sticky);

  padding: var(--space-3) var(--space-4);
  /* Opaque, or rows show through as they scroll under it. */
  background: var(--bg-surface);
  border-bottom: 1px solid var(--border-default);

  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
  color: var(--text-muted);
  white-space: nowrap;
}

/* The whole header cell is the target, so the click area matches the look. */
.sortButton {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);

  margin: calc(var(--space-3) * -1) calc(var(--space-4) * -1);
  padding: var(--space-3) var(--space-4);
  width: calc(100% + var(--space-4) * 2);
  border: 0;
  background: none;
  cursor: pointer;

  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;

  transition: color var(--duration-fast) var(--ease-default);
}

/* text-align can't reach a flex container's items, so the alignment classes have to be
   restated here — otherwise a sortable numeric header sits left while its column is
   right-aligned. */
.center .sortButton {
  justify-content: center;
}

.right .sortButton {
  justify-content: flex-end;
}

.sortButton:hover {
  color: var(--text-primary);
}

/* The cell lifts, not just the label. Coloured on the <th> rather than the button, so it
   fills the cell exactly. Only sortable headers — a plain one isn't a target — and never
   the sorted one, whose gold must hold under the pointer as the selected Tab's does. */
.th:not([aria-sort]):has(.sortButton:hover) {
  background: var(--bg-elevated);
}

.sortButton:focus-visible {
  outline: var(--focus-ring);
  /* Inset: the sticky header clips at the scroller edge. */
  outline-offset: -2px;
}

.sortIcon {
  width: var(--space-3);
  height: var(--space-3);
  flex-shrink: 0;
  opacity: 0.6;
}

/* Both chevrons show while unsorted; one is dropped once a direction is set, and the
   survivor is the state rather than a hint. */
.th[aria-sort] .sortIcon {
  opacity: 1;
}

/* The sorted column reads as the selected Tab: gold label, a wash rising from a glowing
   bar. Same recipe as .tabActive and its indicator, so the two stay one decision.

   The wash fades into the page colour, not the header's — the cell drops back to the
   ground, which is what sets it apart from its neighbours. Still a solid colour
   underneath, because the header is sticky and rows would show through. */
.th[aria-sort] {
  color: var(--active);
  /* Stronger than .tabActive, matched to the location-intelligence reference: the wash
     runs the full height, and an inset glow lifts off the bar. A header cell is shorter
     and darker than a tab, and the Tab's lighter wash all but vanished into it. */
  background:
    linear-gradient(0deg, color-mix(in srgb, var(--active) 9%, transparent), transparent),
    var(--bg-base);
  box-shadow: inset 0 -6px 14px color-mix(in srgb, var(--active) 18%, transparent);
}

/* Hover must not win over sorted, as with the Tab. */
.th[aria-sort] .sortButton {
  color: inherit;
}

/* Sits over the cell's own 1px border rather than above it, so the bar replaces the
   line instead of stacking on it. */
.th[aria-sort]::after {
  content: '';
  position: absolute;
  inset-inline: 0;
  bottom: -1px;
  height: 2px;
  background: var(--active);
  box-shadow:
    0 0 8px 1px color-mix(in srgb, var(--active) 60%, transparent),
    0 -12px 28px color-mix(in srgb, var(--active) 25%, transparent);
  pointer-events: none;
}

/* ── Body ── */

.td {
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--border-subtle);
  color: var(--text-secondary);
  /* No transition, deliberately. The first cell's gradient can't interpolate, so it
     snapped while the rest faded — the row looked like it lagged behind the pointer,
     and a quick sweep left a trail of fading rows. Hover that tracks the eye is instant. */
}

/* Element, not .tr: the loading rows carry no .tr, and still need their last line off. */
.tbody > tr:last-child > .td {
  border-bottom: 0;
}

.clickable {
  cursor: pointer;
}

/* Every body row lights up on hover, clickable or not — it tracks the eye along a wide
   row. A gold stripe on the leading edge, and a wash that's strongest beside it.

   The stripe is an inset shadow on the first cell rather than a pseudo-element on the
   <tr>: positioning inside a table row is where browsers still disagree. */
.tbody .tr:hover .td {
  background: color-mix(in srgb, var(--active) 4%, transparent);
}

.tbody .tr:hover .td:first-child {
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--active) 12%, transparent) 0%,
    color-mix(in srgb, var(--active) 4%, transparent) 100%
  );
  box-shadow: inset 2px 0 0 var(--active);
}

/* Selected is the hover look held on, a step stronger, and a step again under the
   pointer — so hovering a selected row still visibly responds. */
.tbody .selected .td {
  background: color-mix(in srgb, var(--active) 6%, transparent);
  color: var(--text-primary);
}

.tbody .selected .td:first-child {
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--active) 15%, transparent) 0%,
    color-mix(in srgb, var(--active) 6%, transparent) 100%
  );
  box-shadow: inset 2px 0 0 var(--active);
}

.tbody .selected:hover .td {
  background: color-mix(in srgb, var(--active) 8%, transparent);
}

.tbody .selected:hover .td:first-child {
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--active) 18%, transparent) 0%,
    color-mix(in srgb, var(--active) 8%, transparent) 100%
  );
}

/* ── Alignment ── */

.left {
  text-align: left;
}

.center {
  text-align: center;
}

.right {
  text-align: right;
  /* Figures line up on the decimal point rather than drifting with glyph widths. */
  font-variant-numeric: tabular-nums;
}

@media (prefers-reduced-motion: reduce) {
  .sortButton {
    transition: none;
  }
}
